import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export type DiscoveredListing = {
  id: string; listingId: string; keyword: string; page: number; rank: number; url: string; title: string;
  imageUrl: string | null; price: string | null; shopName: string | null; isAd: boolean;
  views: number | null; favorites: number | null; ageDays: number | null; viewsPerDay: number | null; favPerDay: number | null;
  views24: number | null; inCarts: number | null; recentPurchases: number | null; potential: number | null;
  deep: boolean; ruleName: string | null; ruleColor: string | null; scannedAt: string;
  shopId: string | null; shopSales: number | null; shopCreatedAt: string | null;
  prevViews: number | null; prevFavorites: number | null; prevAt: string | null;
  watched: boolean; hidden: boolean;
  aiEval?: AiEval | null;
};

/** AI degerlendirmesi (sunucu: POST /api/discovery/ai-eval) */
export type AiEval = {
  score: number; verdict: "go" | "caution" | "skip";
  copyright: { level: "low" | "medium" | "high"; terms: string[]; note: string };
  why: string; idea: string; evaluatedAt: string;
};

export const fmt = (n: number | null | undefined, digits = 0) => (n == null ? "—" : n.toLocaleString("tr-TR", { maximumFractionDigits: digits }));

// Onceki taramadan (>=12 saat once) bu yana gunluk artis; onceki tarama yoksa null
export function growth(it: DiscoveredListing): { views: number | null; fav: number | null } | null {
  if (!it.prevAt) return null;
  const days = Math.max(0.25, (Date.parse(it.scannedAt) - Date.parse(it.prevAt)) / 86_400_000);
  const per = (now: number | null, prev: number | null) => (now == null || prev == null ? null : (now - prev) / days);
  return { views: per(it.views, it.prevViews), fav: per(it.favorites, it.prevFavorites) };
}

const STOPWORDS = new Set(("and the for with from you your our are this that its not but all any into per off out "
  + "her his him she they them was were has have had will can just").split(" "));

// Baslik kelimeleri: kucuk harf, harf/rakam disinda bol, sayilar + <=2 harf + stopword elenir
export const tokenize = (title: string) =>
  (title.toLowerCase().replace(/i̇/g, "i").match(/[\p{L}\p{N}]+/gu) ?? []).filter((w) => w.length > 2 && !/^\d+$/.test(w) && !STOPWORDS.has(w));

// Baslik benzerligi (Jaccard >= 0.5) ile gruplama; id -> grup uyeleri (yalniz 2+ uyeli gruplar)
export function similarGroups(rows: DiscoveredListing[]): Map<string, string[]> {
  // ponytail: O(n²) ikili karsilastirma; ~2000 ilanda sorun degil, buyurse MinHash/LSH ile aday cifti uret
  const sets = rows.map((r) => new Set(tokenize(r.title)));
  const parent = rows.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = sets[i], b = sets[j];
      if (!a.size || !b.size) continue;
      let inter = 0;
      a.forEach((w) => { if (b.has(w)) inter++; });
      if (inter / (a.size + b.size - inter) >= 0.5) parent[find(i)] = find(j);
    }
  }
  const byRoot = new Map<number, string[]>();
  rows.forEach((r, i) => { const k = find(i); byRoot.set(k, [...(byRoot.get(k) ?? []), r.id]); });
  const out = new Map<string, string[]>();
  byRoot.forEach((ids) => { if (ids.length > 1) ids.forEach((id) => out.set(id, ids)); });
  return out;
}

function Collapsible({ storageKey, title, children }: { storageKey: string; title: string; children: ReactNode }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(storageKey) === "1"; } catch { return false; } });
  const flip = () => { setOpen(!open); try { localStorage.setItem(storageKey, open ? "0" : "1"); } catch { /* */ } };
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
      <button type="button" onClick={flip} aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        <Icon className="h-4 w-4 text-gray-400" />{title}
      </button>
      {open && <div className="border-t border-gray-100 p-4">{children}</div>}
    </div>
  );
}

const pct = (n: number, d: number) => (d ? `%${Math.round((n / d) * 100)}` : "—");
const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b), m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

export function KeywordSummary({ items, onPick }: { items: DiscoveredListing[]; onPick: (keyword: string) => void }) {
  const stats = useMemo(() => {
    const by = new Map<string, DiscoveredListing[]>();
    items.forEach((it) => { if (!it.hidden) by.set(it.keyword, [...(by.get(it.keyword) ?? []), it]); });
    return [...by].map(([keyword, list]) => {
      const pots = list.flatMap((x) => (x.potential == null ? [] : [x.potential]));
      return {
        keyword, count: list.length,
        avgPot: pots.length ? pots.reduce((a, b) => a + b, 0) / pots.length : null,
        fresh: pct(list.filter((x) => x.ageDays != null && x.ageDays <= 60).length, list.length),
        ads: pct(list.filter((x) => x.isAd).length, list.length),
        medViews: median(list.flatMap((x) => (x.viewsPerDay == null ? [] : [x.viewsPerDay]))),
        deep: list.filter((x) => x.deep).length,
      };
    }).sort((a, b) => b.count - a.count);
  }, [items]);
  return (
    <Collapsible storageKey="erc_discovery_kw_open" title="Kelime özeti">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2">Kelime</th>
              <th className="px-3 py-2 text-right">Ürün sayısı</th>
              <th className="px-3 py-2 text-right">Ort. potansiyel</th>
              <th className="px-3 py-2 text-right" title="60 gün ve daha yeni listeler">Yeni liste oranı</th>
              <th className="px-3 py-2 text-right">Reklam oranı</th>
              <th className="px-3 py-2 text-right">Medyan görüntülenme/gün</th>
              <th className="px-3 py-2 text-right">Tam veri sayısı</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((s) => (
              <tr key={s.keyword} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2">
                  <button type="button" onClick={() => onPick(s.keyword)} title="Bu kelimeye göre filtrele"
                    className="rounded-lg text-left font-medium text-indigo-700 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-200">{s.keyword}</button>
                </td>
                <td className="px-3 py-2 text-right">{fmt(s.count)}</td>
                <td className="px-3 py-2 text-right">{fmt(s.avgPot, 1)}</td>
                <td className="px-3 py-2 text-right">{s.fresh}</td>
                <td className="px-3 py-2 text-right">{s.ads}</td>
                <td className="px-3 py-2 text-right">{fmt(s.medViews, 1)}</td>
                <td className="px-3 py-2 text-right">{fmt(s.deep)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Collapsible>
  );
}

export function TopWords({ rows }: { rows: DiscoveredListing[] }) {
  const [copied, setCopied] = useState("");
  const words = useMemo(() => {
    // Potansiyele gore ilk %25 (en az 10 urun)
    const top = [...rows].sort((a, b) => (b.potential ?? -1) - (a.potential ?? -1)).slice(0, Math.max(10, Math.ceil(rows.length / 4)));
    const skip = new Set(top.flatMap((r) => tokenize(r.keyword)));
    const counts = new Map<string, number>();
    top.forEach((r) => new Set(tokenize(r.title)).forEach((w) => { if (!skip.has(w)) counts.set(w, (counts.get(w) ?? 0) + 1); }));
    return [...counts].sort((a, b) => b[1] - a[1]).slice(0, 30);
  }, [rows]);
  const copy = async (w: string) => {
    try { await navigator.clipboard.writeText(w); setCopied(w); setTimeout(() => setCopied(""), 1500); } catch { /* */ }
  };
  return (
    <Collapsible storageKey="erc_discovery_words_open" title="Öne çıkan kelimeler">
      <p className="mb-2 text-xs text-gray-500">Gösterilen ürünlerden potansiyeli en yüksek %25'in başlıklarında geçen kelimeler (aranan kelime hariç). Kopyalamak için tıkla.</p>
      {words.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {words.map(([w, n]) => (
            <button key={w} type="button" onClick={() => copy(w)} title="Kopyala"
              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              {w} · {n}
            </button>
          ))}
          {copied && <span className="text-xs text-emerald-700" role="status">“{copied}” kopyalandı</span>}
        </div>
      ) : <p className="text-sm text-gray-500">Gösterilecek kelime yok.</p>}
    </Collapsible>
  );
}
